/**
 * TranscriptPane — a collapsible pane pinned to the RIGHT of the screenshare
 * that renders the live conversation transcript.
 *
 * Skill-agnostic: it only knows about generic speaker roles ('bot' vs
 * 'participant') and plain text lines; no skill/tool/domain content leaks in.
 *
 * Behavior:
 *  - Expanded: a fixed-width pane with a header (title + collapse toggle) and a
 *    scrollable list (`.scroll-thin`) of speaker-labeled lines, newest at the
 *    bottom. Auto-scrolls to the bottom as lines arrive UNLESS the user has
 *    scrolled up (a "stick to bottom" flag tracked on scroll).
 *  - Empty: a neutral muted message instead of an empty box.
 *  - Collapsed: a thin vertical handle/button that expands the pane on click.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TranscriptLine } from '../hooks/useDisplayChannel';

/** How long (ms) the copy button shows "Copied" feedback after a copy. */
const COPIED_FEEDBACK_MS = 1500;

export interface TranscriptPaneProps {
  lines: TranscriptLine[];
  collapsed: boolean;
  onToggle: () => void;
}

/** How close to the bottom (px) still counts as "stuck to bottom". */
const STICK_THRESHOLD_PX = 24;

/**
 * Format elapsed ms as a compact call-clock stamp: `m:ss` under an hour,
 * `h:mm:ss` once past it. Returns '' for undefined/invalid so lines without
 * a stamp (older frames) render cleanly.
 */
function fmtElapsed(ms?: number): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const ss = String(sec).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

export function TranscriptPane({ lines, collapsed, onToggle }: TranscriptPaneProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Whether we should keep pinning the view to the newest line. Starts true so
  // the first lines are visible; flips to false when the user scrolls up.
  const [stickToBottom, setStickToBottom] = useState(true);

  // Track the user's scroll position: they are "stuck to bottom" only while the
  // scroll offset is within a small threshold of the very bottom.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (el === null) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStickToBottom(distanceFromBottom <= STICK_THRESHOLD_PX);
  };

  // On new lines (or on expand), keep the latest line visible when stuck.
  // useLayoutEffect so the jump happens before paint (no visible flicker).
  useLayoutEffect(() => {
    if (collapsed) return;
    const el = scrollRef.current;
    if (el === null) return;
    if (stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
    // Re-run whenever the number of lines changes or the pane re-expands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.length, collapsed]);

  // When the pane is re-expanded, snap back to the bottom so the newest line is
  // shown immediately.
  useEffect(() => {
    if (!collapsed) setStickToBottom(true);
  }, [collapsed]);

  // Brief "Copied" feedback after copying the transcript to the clipboard.
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending feedback timer on unmount.
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  // Copy the entire transcript as plain text, one line per entry:
  // "Assistant: …" / "You: …". No-op when there are no lines.
  const handleCopy = () => {
    if (lines.length === 0) return;
    const text = lines
      .map((line) => `${line.role === 'bot' ? 'Assistant' : 'You'}: ${line.text}`)
      .join('\n');
    void navigator.clipboard?.writeText(text);
    setCopied(true);
    if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  if (collapsed) {
    // Minimal collapsed state: a thin vertical handle on the right edge.
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-label="Expand transcript"
        title="Expand transcript"
        className="flex w-8 shrink-0 flex-col items-center justify-center gap-2 border-l border-border bg-surface text-content-muted transition-colors hover:bg-surface-raised hover:text-content"
      >
        <span className="text-sm leading-none">‹</span>
        <span
          className="text-xs tracking-wide"
          style={{ writingMode: 'vertical-rl' }}
        >
          Transcript
        </span>
      </button>
    );
  }

  return (
    <aside className="flex w-80 shrink-0 flex-col overflow-hidden min-h-0 border-l border-border bg-surface">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-content">Transcript</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            disabled={lines.length === 0}
            aria-label={copied ? 'Copied' : 'Copy transcript'}
            title={copied ? 'Copied' : 'Copy transcript'}
            className="rounded px-2 py-1 text-content-muted transition-colors hover:bg-surface-raised hover:text-content disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-content-muted"
          >
            {copied ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onToggle}
            aria-label="Collapse transcript"
            title="Collapse transcript"
            className="rounded px-2 py-1 text-sm leading-none text-content-muted transition-colors hover:bg-surface-raised hover:text-content"
          >
            ›
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="scroll-thin flex-1 overflow-y-auto px-4 py-3"
      >
        {lines.length === 0 ? (
          <p className="text-sm text-content-muted">No transcript yet.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {lines.map((line, i) => {
              const isBot = line.role === 'bot';
              const stamp = fmtElapsed(line.elapsedMs);
              return (
                <li key={i} className="text-sm leading-relaxed">
                  {stamp && (
                    <span
                      className="mr-2 font-mono text-xs tabular-nums text-content-muted"
                      title="Elapsed since call start"
                    >
                      {stamp}
                    </span>
                  )}
                  <span
                    className={
                      isBot
                        ? 'mr-2 font-semibold text-accent'
                        : 'mr-2 font-semibold text-brand-violet'
                    }
                  >
                    {isBot ? 'Assistant' : 'You'}
                  </span>
                  <span className="text-content">{line.text}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
